import { Badge, type BadgeTone } from '../atoms/Badge';
import { Card } from '../atoms/Card';
import { Icon } from '../atoms/Icon/Icon';
import { Inline } from '../atoms/Inline';
import { Stack } from '../atoms/Stack';
import { Heading, Text } from '../atoms/Typography';
import { Section } from '../templates/Section';
import { useTranslation } from '../../i18n/useTranslation';
import type {
  ZeovitConcernAssessment,
  ZeovitDosingReviewEntry,
  ZeovitDosingVerdict,
  ZeovitRecommendation,
} from '../../services/zeovitRecommendationService';
import './ZeovitRecommendationReview.css';

/** Verdict → Badge tone + decorative icon. The Badge text always carries the meaning. */
const VERDICT_PRESENTATION: Record<ZeovitDosingVerdict, { tone: BadgeTone; icon: string }> = {
  keep: { tone: 'neutral', icon: 'check' },
  adjust: { tone: 'info', icon: 'wrench' },
  stop: { tone: 'danger', icon: 'minus' },
};

function formatDose(amount: number | null, unit: string, language: string): string {
  if (amount === null) return unit;
  return `${new Intl.NumberFormat(language, { maximumFractionDigits: 2 }).format(amount)} ${unit}`.trim();
}

/** "Your observations": the reported concerns, the note and the AI's assessment per concern. */
export function ZeovitObservationsSection({ recommendation }: { recommendation: ZeovitRecommendation }) {
  const { t } = useTranslation();
  const concerns = recommendation.concerns ?? [];
  const note = recommendation.concernNote ?? null;
  if (concerns.length === 0 && !note) return null;

  const assessments = new Map<string, ZeovitConcernAssessment>(
    (recommendation.concernAssessment ?? []).map((entry) => [entry.concern, entry] as const),
  );
  const keys: Array<ZeovitConcernAssessment['concern']> = [...concerns, ...(note ? (['other'] as const) : [])];

  return (
    <Section title={t('zeovit.detail.observations.title')} data-testid="zeovit-observations">
      <Stack gap="4">
        {note ? (
          <Card>
            <Text>{t('zeovit.detail.observations.note', { note })}</Text>
          </Card>
        ) : null}
        <Stack as="ul" gap="4" className="zeovit-review__list">
          {keys.map((concern) => {
            const entry = assessments.get(concern);
            return (
              <li key={concern}>
                <Card>
                  <Stack gap="2">
                    <Heading level={3}>{t(`zeovit.concern.${concern}`)}</Heading>
                    {entry && !entry.notAssessed ? (
                      <>
                        {entry.assessment ? (
                          <Text>{t('zeovit.detail.observations.assessment', { text: entry.assessment })}</Text>
                        ) : null}
                        {entry.actions ? (
                          <Text>{t('zeovit.detail.observations.actions', { text: entry.actions })}</Text>
                        ) : null}
                      </>
                    ) : (
                      <Text color="muted">{t('zeovit.detail.observations.notAssessed')}</Text>
                    )}
                  </Stack>
                </Card>
              </li>
            );
          })}
        </Stack>
      </Stack>
    </Section>
  );
}

function ReviewItem({ entry }: { entry: ZeovitDosingReviewEntry }) {
  const { t, i18n } = useTranslation();
  const presentation = VERDICT_PRESENTATION[entry.verdict];
  return (
    <li>
      <Card data-testid="zeovit-review-item">
        <Stack gap="2">
          <Inline justify="between" align="center" wrap>
            <Text weight="semibold">{entry.productName}</Text>
            <Badge tone={presentation.tone}>
              <Icon name={presentation.icon} size="sm" />
              {t(`zeovit.detail.review.verdict.${entry.verdict}`)}
            </Badge>
          </Inline>
          <Text color="muted" size="sm">
            {t('zeovit.detail.review.amount', { dose: formatDose(entry.amount, entry.unit, i18n.language) })}
          </Text>
          {entry.reason ? <Text>{entry.reason}</Text> : null}
          {entry.notAssessed ? <Text color="muted">{t('zeovit.detail.review.notAssessed')}</Text> : null}
          {entry.deferred ? <Text color="muted">{t('zeovit.detail.review.deferred')}</Text> : null}
        </Stack>
      </Card>
    </li>
  );
}

/**
 * "Assessment of your current dosing": every plan that was active when the
 * recommendation was created, with its verdict as text + Badge (never colour-only),
 * and a separate "Omission recommended" group. Not shown for recommendations created
 * before the review existed.
 */
export function ZeovitDosingReviewSection({ recommendation }: { recommendation: ZeovitRecommendation }) {
  const { t } = useTranslation();
  const review = recommendation.dosingReview;
  if (!Array.isArray(review)) return null;

  const stops = review.filter((entry) => entry.verdict === 'stop');
  const others = review.filter((entry) => entry.verdict !== 'stop');

  return (
    <Section
      title={t('zeovit.detail.review.title')}
      description={t('zeovit.detail.review.description')}
      data-testid="zeovit-dosing-review"
    >
      {review.length === 0 ? (
        <Card>
          <Text color="muted">{t('zeovit.detail.review.empty')}</Text>
        </Card>
      ) : (
        <Stack gap="6">
          {stops.length > 0 ? (
            <Stack gap="3" data-testid="zeovit-review-stop-group">
              <Heading level={3}>{t('zeovit.detail.review.stopGroup')}</Heading>
              <Stack as="ul" gap="3" className="zeovit-review__list">
                {stops.map((entry) => (
                  <ReviewItem key={entry.planRef} entry={entry} />
                ))}
              </Stack>
            </Stack>
          ) : null}
          {others.length > 0 ? (
            <Stack gap="3">
              <Heading level={3}>{t('zeovit.detail.review.otherGroup')}</Heading>
              <Stack as="ul" gap="3" className="zeovit-review__list">
                {others.map((entry) => (
                  <ReviewItem key={entry.planRef} entry={entry} />
                ))}
              </Stack>
            </Stack>
          ) : null}
        </Stack>
      )}
    </Section>
  );
}
